import * as React from 'react'
import { trackActivity } from '../activity'
import { jsonFetch } from '../bookingApi'
import { clearBookingSessionStorage } from '../bookingSession'
import { confirmationPresentation } from '../confirmationPresentation'
import type { BookingStatus } from '../types'

function waitForRetry(signal: AbortSignal) {
    return new Promise<void>(resolve => {
        const finish = () => { window.clearTimeout(timer); signal.removeEventListener('abort', finish); resolve() }
        const timer = window.setTimeout(finish, 2000)
        signal.addEventListener('abort', finish, { once: true })
        if (signal.aborted) finish()
    })
}

export function useConfirmationPolling(apiBaseUrl: string, onStatus?: (status: BookingStatus) => void) {
    const [status, setStatus] = React.useState<BookingStatus | null>(null)
    const [checking, setChecking] = React.useState(false)
    const [error, setError] = React.useState('')
    const controller = React.useRef<AbortController | null>(null)
    const callback = React.useRef(onStatus)
    callback.current = onStatus
    React.useEffect(() => () => controller.current?.abort(), [])

    const check = React.useCallback(async (lookup: Record<string, string>) => {
        controller.current?.abort()
        const request = new AbortController()
        controller.current = request
        setChecking(true)
        setError('')
        try {
            for (let attempt = 0; attempt < 30 && !request.signal.aborted; attempt += 1) {
                try {
                    const next = await jsonFetch<BookingStatus>(`${apiBaseUrl.replace(/\/$/, '')}/v1/booking-status`, {
                        method: 'POST', headers: { 'Content-Type': 'application/json' },
                        body: JSON.stringify(lookup), signal: request.signal,
                    })
                    if (request.signal.aborted) return
                    setStatus(next)
                    callback.current?.(next)
                    const view = confirmationPresentation(next)
                    if (view.successfulPayment) {
                        trackActivity('confirmation_viewed', {}, true)
                        clearBookingSessionStorage()
                    }
                    if (view.final) return
                } catch {
                    if (attempt === 29 && !request.signal.aborted) {
                        setError('We couldn’t check the latest payment status. Please check again or contact us. Don’t make another payment while we confirm it.')
                    }
                }
                if (attempt < 29) await waitForRetry(request.signal)
            }
        } finally {
            if (!request.signal.aborted) setChecking(false)
        }
    }, [apiBaseUrl])
    return { status, checking, error, check }
}
